import React from 'react';
import cl from 'classnames';

import { ButtonHelper } from '../buttons/ButtonHelper';
import { Translate } from '../Translate';
import { SlashCommand } from '../../utils/useSlashCommandInput';
import { messagesKeys } from '../../locales/keys';

interface CommandDropdownProps<T extends SlashCommand> {
  items: T[];
  onSelect: (item: T) => void;
  className?: string;
}

function CommandDropdown<T extends SlashCommand>({
  items,
  onSelect,
  className
}: CommandDropdownProps<T>) {
  if (items.length === 0) {
    return null;
  }

  return (
    <ul
      style={{ maxHeight: '16rem' }}
      className={cl(
        'absolute bottom-full left-0 mb-1 min-w-48 w-full z-20 py-1 overflow-y-auto bg-white border border-transparent dark:bg-gray-700 dark:border-opacity-10 dark:border-white dark:text-white focus:outline-none ring-1 ring-black ring-opacity-5 rounded-md shadow-lg text-gray-700',
        className
      )}
    >
      <li className="pt-2.5 px-3 pb-1.5" aria-hidden="true">
        <div className="text-gray-400 dark:text-gray-400 text-2xs leading-normal">
          <Translate id={messagesKeys.runCommand} />
        </div>
        <hr className="border-gray-200 dark:border-gray-600 mt-0.5" />
      </li>

      {items.map((item) => (
        <li
          key={item.id}
          role="option"
          aria-selected={false}
          className="px-1 block group/menu-item cursor-pointer"
        >
          <ButtonHelper
            className="block w-full text-left px-2 py-0.5 dark:group-hover/menu-item:bg-gray-800 group-hover/menu-item:bg-gray-100 rounded min-h-7"
            onClick={() => onSelect(item)}
          >
            <div className="flex items-center gap-2 text-md sm:text-sm">
              <span className="mr-auto font-normal sm:font-medium block truncate">
                {item.title}
              </span>
            </div>
            {item.description && (
              <div className="text-2xs 3xl:text-xs text-left text-gray-500 dark:text-gray-400">
                /{item.alias} • {item.description}
              </div>
            )}
          </ButtonHelper>
        </li>
      ))}
    </ul>
  );
}

export default CommandDropdown;
